import React, { useMemo } from 'react';
import { DataTable, Column } from '@/components/ui/data-table';
import { StatusBadge, getStatusAccentColor } from '@/components/ui/table-components';
import { CandidateMatches } from '../../interfaces';
import { formatMatchStatusLabel } from '@/utils/matchStatus';

interface CandidateMatchesTableProps {
  matches: CandidateMatches[];
  onMatchClick: (match: CandidateMatches) => void;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  onSort?: (columnId: string) => void;
  isLoading?: boolean;
}

export function CandidateMatchesTable({
  matches,
  onMatchClick,
  sortBy,
  sortDirection,
  onSort,
  isLoading = false,
}: CandidateMatchesTableProps) {
  const columns: Column<CandidateMatches>[] = useMemo(
    () => [
      {
        id: 'candidateName',
        header: 'Candidate',
        sortable: true,
        cell: (match) => (
          <div className="font-medium text-slate-900 dark:text-slate-100">
            {match.candidateName || '-'}
          </div>
        ),
      },
      {
        id: 'candidateJobTitle',
        header: 'Candidate Title',
        sortable: true,
        cell: (match) => (
          <span className="text-slate-700 dark:text-slate-300">
            {match.candidateJobTitle || '-'}
          </span>
        ),
      },
      {
        id: 'candidateLocation',
        header: 'Candidate Location',
        sortable: true,
        cell: (match) => (
          <span className="text-slate-700 dark:text-slate-300">
            {match.candidateLocation?.displayName || '-'}
          </span>
        ),
      },
      {
        id: 'jobTitle',
        header: 'Role',
        sortable: true,
        cell: (match) => (
          <div className="font-medium text-slate-900 dark:text-slate-100">
            {match.jobTitle || '-'}
          </div>
        ),
      },
      {
        id: 'currentStatus',
        header: 'Status',
        sortable: true,
        className: 'text-center',
        cell: (match) => (
          <StatusBadge status={formatMatchStatusLabel(match.currentStatus || 'UNKNOWN')} />
        ),
      },
    ],
    []
  );

  const getRowId = (match: CandidateMatches, index: number) => {
    if (match?.id) {
      return `candidate-match-${match.id}`;
    }
    return `candidate-match-idx-${index}`;
  };

  return (
    <DataTable
      data={matches}
      columns={columns}
      onRowClick={onMatchClick}
      getRowId={getRowId}
      getRowAccentColor={(match) => getStatusAccentColor(match.currentStatus)}
      sortBy={sortBy}
      sortDirection={sortDirection}
      onSort={onSort}
      isLoading={isLoading}
      emptyMessage="No candidate matches yet."
    />
  );
}
